import React, { useContext, useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { formatCurrencyAutoLocale } from '../../utils/tools'
import { SettingsContext } from '../../context/settings-context'

const accountKey = (account) => JSON.stringify([account.business_id ?? null, String(account.id)])

const AddAccountModal = ({ accounts = [], setConfirmDisabled, setSelectedAccount }) => {
    const [selectedId, setSelectedId] = useState(null)
    const { theme } = useContext(SettingsContext)
    // Resolve against the latest payload: refresh may remove or disable a selection.
    const selected = accounts.find((account) =>
        account.id != null && accountKey(account) === selectedId && account.usable !== false,
    ) || null

    useEffect(() => {
        setConfirmDisabled(!selected)
        setSelectedAccount(selected)
        if (!selected) setSelectedId(null)
    }, [selected, setConfirmDisabled, setSelectedAccount])

    useEffect(() => {
        document.body.classList.remove('light', 'dark')
        document.body.classList.add(theme)
    }, [theme])

    // A Business Manager can see ad accounts the signed-in user cannot create ads in.
    // Picking one used to succeed here and fail much later at publish time with Meta error
    // 200/1815066. Such rows stay visible - so the user can see the account exists and why
    // it's unavailable - but cannot be selected. `usable !== false` keeps payloads cached
    // before the field existed selectable.
    const isUsable = (account) => account?.usable !== false

    const rowClass = (account) =>
        [selected === account ? 'selected-row' : '', isUsable(account) ? '' : 'opacity-50']
            .filter(Boolean)
            .join(' ')

    const selectAccount = (account) => {
        if (!isUsable(account)) return
        setSelectedId(accountKey(account))
    }

    return (
        <Table hover responsive className="m-n4 mb-0 w-100 table table table-header-style">
            <thead>
                <tr>
                    <th></th>
                    <th>Status</th>
                    <th>Account Name</th>
                    <th>Monthly Spend</th>
                </tr>
            </thead>
            <tbody>
                {accounts.map((account, index) => (
                    <tr
                        key={index}
                        style={{ cursor: isUsable(account) ? 'pointer' : 'not-allowed' }}
                        className={rowClass(account)}
                        title={isUsable(account) ? undefined : account.unusable_reason || undefined}
                        onClick={() => selectAccount(account)}
                    >
                        <td className="align-middle text-start" style={{ width: '1%' }}>
                            <input
                                type="radio"
                                name="metaAccount"
                                className="custom-radio"
                                id={`select-${account.id}`}
                                disabled={!isUsable(account)}
                                checked={selected === account}
                                onChange={() => selectAccount(account)}
                            />
                        </td>
                        <td className="align-middle">
                            <div className="d-flex align-items-center gap-2">
                                {account?.tos_accepted ? (
                                    <>
                                        <span className="status-active-dot">●</span>
                                        <span className="text-active">Active</span>
                                    </>
                                ) : (
                                    <div
                                        className={`d-flex align-items-center gap-1 ${theme === 'dark' ? 'tos-dark' : 'tos-light'}`}
                                    >
                                        <span className="status-dot">●</span>
                                        <span className="account-cell">Accept ToS</span>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            />
                                            <path
                                                d="M12 13L12 8"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <path
                                                d="M12 15.75L12 16"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </td>

                        <td className="align-middle account-cell">
                            <div>{account.id?.replace('act_', '') || ''} - {account.name}</div>
                            {/* Why this account can't be used, so the user can fix it in
                                Meta instead of hitting a permissions error at publish. */}
                            {!isUsable(account) && account.unusable_reason && (
                                <div className="small text-muted">{account.unusable_reason}</div>
                            )}
                        </td>
                        <td className="align-middle account-cell">
                            {formatCurrencyAutoLocale({
                                amount: account.month_spend,
                                currency: account.currency,
                                showCode: true,
                            })}
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

export default AddAccountModal
